// spell-vfx-marker.ts — the object card's cast preview (2026-09-30).
//
// A spell/ability whose browser-compiled array carries a saved published VFX recipe (assets.magic_vfx)
// gets a header marker beside its PL pill. Clicking it opens a modal
// where a basic character casts the spell at a training dummy — the same Ft3dMagicVfxRuntime the encounter
// plays, so devs review the real effect and players see what the spell looks like.
//
// The recipe lives on the spell itself (Fire Bolt) or on a child ability it creates (Magic Missile → Magic
// Missile Projectile); the authored catalog and browser compiler supply the child definition.
//
// SIGNED-IN ONLY: window.FateTide.spellVfxPreviewUrl is '' for a signed-out viewer, which hides the marker.
// LAZY: three.js, the character GLB and the VFX runtime live in their own bundle (spell-vfx-preview.js),
// imported on the first click — catalog pages never pay for WebGL.

import { layerEntriesOf, layerOf, layerListEntryUuid } from '../shared/array-types';
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import { compilePresentationDefinitions, loadSummonPreview, presentationDefinition, summonPreview, summonPreviewIds } from '../janus-engine/display/presentation-inputs-20';
import type { SpellVfxPreviewHandle, SpellVfxPreviewPayload, SpellVfxPreviewZone } from '../shared/spell-vfx-preview-payload';

type Blob = Record<string, unknown>;

const MODAL_ID = 'ft-spell-vfx-modal';
const STYLE_ID = 'ft-spell-vfx-style';
const SELF_RANGE = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const REACH_RANGE = '36D59FF4-6778-96ED-8664-F5D63638C77B';
/** The Obscured status (encounter-map/ft3d/perception.ts PERCEPTION_STATUS.obscured; not imported — that module is heavy). */
const OBSCURED_STATUS = 'E2054884-038E-3D6E-56C5-19ABBFED8379';
/** Layers that attach a child ability to its spell. */
const CHILD_ABILITY_LAYERS = ['free_ability', 'full_ability', 'ability', 'quick_ability', 'reaction_ability', 'charged_spell', 'charged_ability'];
/** Layers a charged item (a wand) casts through; the only child layers followed on a card that is not itself castable.
 * A wand's charges come from its enchantment rows, so they sit at the array ROOT (item.charged_spell), not in item_layer_types. */
const CHARGED_LAYERS = new Set(['charged_spell', 'charged_ability']);
/** Card types whose child abilities ARE the cast (Magic Missile → its Projectile). An ancestry, class or feat that merely
 * grants an ability with an effect is not a cast, so it gets no marker — nor does a passive that grants one. */
const CASTABLE_TYPES = new Set(['spell', 'menta', 'curse', 'sanguine', 'ability', 'full_ability', 'quick_ability', 'free_ability',
    'reaction_ability', 'attack_ability', 'opportunity_attack_ability', 'lair_ability', 'trap_ability']);
/** Physical items (gear): a weapon, shield or potion's granted ability is something the item does when used, so it
 * previews too. Mirror of ft_object_item_types() (item_layer_save_lib.php) — keep the two lists in sync. */
const OBJECT_TYPES = new Set(['adventuring_gear', 'ammo', 'armor', 'clothing', 'tools', 'container', 'equipment',
    'explosive', 'furniture', 'food', 'drink', 'gaming_set', 'gemstone', 'instrument', 'monster_bit', 'potion', 'coating',
    'elixir', 'scroll', 'book', 'poison', 'spell_material_component', 'spellcasting_focus', 'tack_and_harness', 'tattoo',
    'tool', 'weapon', 'reagent', 'trade_good', 'ore_vein', 'ore', 'ingot', 'plant', 'herb', 'tree', 'timber', 'plank', 'hide',
    'enchanting_dust', 'jewelry', 'light_source', 'door', 'window', 'silverware', 'building', 'barding', 'seed', 'food_plant',
    'herbal_plant', 'artwork', 'digsite', 'junk']);

let openToken = 0;
let activeHandle: SpellVfxPreviewHandle | null = null;

function recordOf(value: unknown): Blob | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Blob : null;
}

function col(arr: Blob, key: string): string {
    return String(recordOf(arr.items)?.[key] ?? arr[key] ?? '');
}

function previewUrl(): string {
    const ft = (window as unknown as { FateTide?: { spellVfxPreviewUrl?: string } }).FateTide;
    return typeof ft?.spellVfxPreviewUrl === 'string' ? ft.spellVfxPreviewUrl : '';
}

function recipeOf(arr: Blob | null): Blob | null {
    const recipe = recordOf(recordOf(arr?.assets)?.magic_vfx);
    return recipe && recipe.schema === 'ft.vfx.recipe.v1' && recipe.kind !== 'none' ? recipe : null;
}

function catalogDef(root: Blob, id: string): Blob | null {
    const catalog = recordOf(root.catalog);
    if (!catalog || !id) return null;
    return recordOf(catalog[id]) ?? recordOf(catalog[id.toUpperCase()]) ?? recordOf(catalog[id.toLowerCase()]);
}

/** The item that carries the recipe: the card's item, else the first child ability that does (charged layers only
 * when the card is neither a spell/ability nor a physical item). */
function vfxSourceOf(arr: Blob): { source: Blob; recipe: Blob } | null {
    const own = recipeOf(arr);
    if (own) return { source: arr, recipe: own };
    const layers = recordOf(arr.item_layer_types) ?? {};
    const type = col(arr, 'item_type').toLowerCase();
    const castable = CASTABLE_TYPES.has(type) || OBJECT_TYPES.has(type);
    for (const key of CHILD_ABILITY_LAYERS) {
        if (!castable && !CHARGED_LAYERS.has(key)) continue;
        const list = (slot: unknown): unknown[] => (Array.isArray(slot) ? slot : slot == null ? [] : [slot]);
        // Both places: an empty layer slot must not hide the root charges.
        const entries = [...list(layers[key]), ...(CHARGED_LAYERS.has(key) ? list(arr[key]) : [])];
        for (const entry of entries) {
            const def = catalogDef(arr, layerListEntryUuid(entry));
            const recipe = recipeOf(def);
            if (def && recipe) return { source: def, recipe };
        }
    }
    return null;
}

function num(value: unknown, fallback: number): number {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
}

function truthy(value: unknown): boolean {
    return value === true || (value != null && value !== '' && value !== '0' && value !== 0 && value !== false);
}

/** The persistent area the cast leaves standing (persistent_aoe on the area holder), as the encounter would draw it. */
function zoneOf(holder: Blob): SpellVfxPreviewZone | null {
    if (!truthy(layerOf(holder, 'persistent_aoe'))) return null;
    const aoeType = String(layerOf(holder, 'aoe_type') ?? '');
    const distance = num(layerOf(holder, 'aoe_distance'), 0);
    if (!aoeType || !(distance > 0)) return null;
    const damage = layerListEntryUuid(layerOf(holder, 'damage_type') ?? '');
    return {
        aoeType, distance, depth: num(layerOf(holder, 'aoe_distance_depth'), 1), height: num(layerOf(holder, 'aoe_distance_height'), 1),
        followsTarget: truthy(layerOf(holder, 'aoe_follows_target')),
        damageType: damage || null,
        obscures: layerEntriesOf(holder, 'status_effect_applied_to_target_after_contesting_roll_failed')
            .some((entry) => layerListEntryUuid(entry).toUpperCase() === OBSCURED_STATUS),
    };
}

function payloadOf(arr: Blob): SpellVfxPreviewPayload | null {
    const type = col(arr, 'item_type').toLowerCase();
    const summonItemIds = CASTABLE_TYPES.has(type) || OBJECT_TYPES.has(type) ? summonPreviewIds(arr) : [];
    const summon = summonItemIds.length ? summonPreview(arr, id => presentationDefinition(arr, id)) : null;
    // A summon always has an arrival to show (its portal and rise), even with no cast effect of its own.
    const activeAbility = CASTABLE_TYPES.has(type) && (type === 'ability' || type.endsWith('_ability'));
    const found = vfxSourceOf(arr) ?? (summonItemIds.length ? { source: arr, recipe: summon?.rise ?? { kind: 'none' } }
        : activeAbility ? { source: arr, recipe: { kind: 'none' } } : null);
    if (!found) return null;
    const { source, recipe } = found;
    const zone = zoneOf(layerOf(source, 'aoe_type') != null ? source : arr);
    let area: SpellVfxPreviewPayload['area'] = null;
    if (recipe.kind === 'area') {
        // The area's own size: the recipe carrier's aoe layers first (a child ability can own the area), else the spell's.
        const holder = layerOf(source, 'aoe_type') != null ? source : arr;
        const aoeType = String(layerOf(holder, 'aoe_type') ?? '');
        const distance = num(layerOf(holder, 'aoe_distance'), 0);
        if (aoeType && distance > 0) {
            area = { aoeType, distance, depth: num(layerOf(holder, 'aoe_distance_depth'), 1), height: num(layerOf(holder, 'aoe_distance_height'), 0) };
        }
    }
    const range = String(layerOf(source, 'range') ?? '').toUpperCase();
    const selfRange = range === SELF_RANGE;
    const selfCast = selfRange
        // ...and a Self aura that rides the caster (Spirit Guardians) centres on the caster too.
        && (layerOf(source, 'aoe_type') == null || zone?.followsTarget === true) && !['beam', 'projectile'].includes(String(recipe.kind));
    return {
        itemId: col(arr, 'item_id'),
        itemName: String(decodeHtmlEntitiesForPlainText(col(arr, 'item_name')) ?? ''),
        sourceId: col(source, 'item_id'),
        sourceName: String(decodeHtmlEntitiesForPlainText(col(source, 'item_name')) ?? ''),
        recipe,
        ...(recipe.kind === 'none' && !summonItemIds.length ? { motionOnly: true } : {}),
        // The effect carrier's range: Magic Missile itself is Self (it grants the Projectile ability), the Projectile is ranged.
        // A Self-range AREA (Chromatic Beam's line) emanates from the caster at others, and a beam or projectile always
        // needs another target — only the rest are cast on oneself.
        selfCast,
        touchRange: range === REACH_RANGE,
        // A Self-range AREA cast at others (Holy Flare, Sanctuary of the Last Flame) erupts AROUND the caster and catches
        // whoever stands in it — Janus centres a Self-range placed area on the caster (expand-placed-aoe-targets.ts).
        areaFromCaster: selfRange && !selfCast && (area !== null || !!zone),
        area,
        zone,
        summon,
        ...(summonItemIds.length ? { summonItemIds, summonTransformsCorpse: truthy(layerOf(arr, 'summon_transforms_targeted_corpse')) } : {}),
        audio: recordOf(arr.assets)?.audio ?? null,
    };
}

/** Header marker for the object card's tag row, left of the PL pill; '' when the viewer is signed out or there is no effect. */
export function renderSpellVfxMarker(arr: Blob): string {
    if (!previewUrl()) return '';
    const payload = payloadOf(arr);
    if (!payload) return '';
    const tip = payload.motionOnly ? 'Preview the action'
        : payload.sourceId && payload.sourceId !== payload.itemId
        ? `Preview the cast effect (${payload.sourceName})`
        : 'Preview the cast effect';
    return `<span class="position-relative d-inline-block align-middle me-2" role="button" data-ft-oc-action="open-spell-vfx"`
        + ` data-ft-vfx-payload="${escapeHtml(JSON.stringify(payload))}">`
        + `${iconMap('mesh', 20, 20, tip)}`
        + '<span class="position-absolute badge rounded-pill bg-info text-dark px-1"'
        + ' style="right:-5px;bottom:-3px;font-size:.55rem;line-height:1" aria-hidden="true">&#9654;</span>'
        + `<span class="visually-hidden">${payload.motionOnly ? 'Preview the action' : 'Preview the cast effect'}</span></span> `;
}

const STYLE = `
#${MODAL_ID} .modal-content {
	background: #111720;
	background-image: linear-gradient(135deg, rgba(34, 211, 238, 0.065) 0%, rgba(148, 163, 184, 0.025) 65%);
	border: 1px solid rgba(34, 211, 238, 0.32);
	border-radius: 0.85rem;
	color: #e2e8f0;
}
#${MODAL_ID} .modal-header {
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 0.85rem 0.85rem 0 0;
	background: linear-gradient(100deg, color-mix(in srgb, #22d3ee 38%, #0b0e16) 0%, color-mix(in srgb, #22d3ee 18%, #0b0e16) 55%, #111720 100%);
	box-shadow: inset 4px 0 0 #22d3ee;
	color: #f8fafc;
}
#${MODAL_ID} .modal-title { font-weight: 700; letter-spacing: 0.025em; }
#${MODAL_ID} .ft-sv-stage { position: relative; height: min(62vh, 520px); border-radius: 0.6rem; overflow: hidden; background: #111a29; }
#${MODAL_ID} .ft-sv-stage canvas { display: block; width: 100%; height: 100%; touch-action: none; }
#${MODAL_ID} .ft-sv-state { position: absolute; left: 0.75rem; top: 0.6rem; color: #aee6d3; font-size: 0.8rem; pointer-events: none; }
#${MODAL_ID} .ft-sv-controls { display: flex; flex-wrap: wrap; gap: 0.5rem 1rem; align-items: center; margin-top: 0.75rem; font-size: 0.85rem; color: #cbd5e1; }
#${MODAL_ID} .ft-sv-controls select { width: auto; }
#${MODAL_ID} .ft-sv-hint { color: #94a3b8; font-size: 0.75rem; margin-left: auto; }
`;

function ensureModal(): HTMLElement {
    if (!document.getElementById(STYLE_ID)) {
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = STYLE;
        document.head.appendChild(style);
    }
    const existing = document.getElementById(MODAL_ID);
    if (existing) return existing;
    const el = document.createElement('div');
    el.id = MODAL_ID;
    el.className = 'modal fade';
    el.tabIndex = -1;
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = `
        <div class="modal-dialog modal-xl modal-dialog-centered" style="max-width:min(1100px,94vw)">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title mb-0" data-ft-sv-title>Cast preview</h5>
                    <button type="button" class="btn-close btn-close-white ms-auto" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body" data-ft-sv-body></div>
            </div>
        </div>`;
    document.body.appendChild(el);
    // Closing the modal releases the WebGL context, the model and the audio.
    el.addEventListener('hidden.bs.modal', () => { openToken++; activeHandle?.dispose(); activeHandle = null; });
    return el;
}

type PreviewModule = {
    mountSpellVfxPreview(host: HTMLElement, payload: SpellVfxPreviewPayload, onHandle?: (handle: SpellVfxPreviewHandle) => void): Promise<SpellVfxPreviewHandle>;
};
let modulePromise: Promise<PreviewModule> | null = null;

/** Open the cast preview for the marker's payload. Safe to call repeatedly. */
export async function openSpellVfxPreview(el: HTMLElement): Promise<void> {
    const url = previewUrl();
    let payload: SpellVfxPreviewPayload;
    try { payload = JSON.parse(el.dataset.ftVfxPayload || ''); } catch { return; }
    if (!url || !payload?.recipe) return;
    const token = ++openToken;
    activeHandle?.dispose();
    activeHandle = null;
    const modal = ensureModal();
    const title = modal.querySelector('[data-ft-sv-title]');
    const body = modal.querySelector<HTMLElement>('[data-ft-sv-body]');
    if (title) title.textContent = `${payload.motionOnly ? 'Action' : 'Cast'} preview — ${payload.itemName}`;
    if (!body) return;
    body.innerHTML = '<p class="mb-0" style="color:#94a3b8">Loading the preview…</p>';
    const bs = (window as unknown as { bootstrap?: { Modal?: { getOrCreateInstance(el: Element): { show(): void } } } }).bootstrap;
    if (bs?.Modal) bs.Modal.getOrCreateInstance(modal).show();
    else { modal.classList.add('show'); modal.style.display = 'block'; }
    try {
        modulePromise ??= import(/* @vite-ignore */ url) as Promise<PreviewModule>;
        const mod = await modulePromise;
        if (token !== openToken) return;
        if (payload.summonItemIds?.length) {
            const owner = window as unknown as { fetchAndCacheObject?: (id: string, options?: { authoredCard: boolean }) => Promise<Blob | null>;
                FateTide?: { objectCache?: Map<string, Blob> } };
            if (!owner.fetchAndCacheObject) throw new Error('Summon preview definition reader unavailable');
            const cached = owner.FateTide?.objectCache?.get(payload.itemId) ?? owner.FateTide?.objectCache?.get(payload.itemId.toUpperCase());
            const parent = cached && recordOf(cached._entity_base) ? cached : await owner.fetchAndCacheObject(payload.itemId, { authoredCard: true });
            if (!parent) throw new Error('Summon preview parent unavailable');
            const definitions = await compilePresentationDefinitions(parent, payload.summonItemIds);
            const item: Blob = { items: { item_id: payload.itemId, item_name: payload.itemName },
                item_layer_types: { summon: payload.summonItemIds, summon_transforms_targeted_corpse: payload.summonTransformsCorpse ? 1 : 0 },
                assets: { magic_vfx: payload.sourceId === payload.itemId ? payload.recipe : null } };
            const summon = await loadSummonPreview(item, async id => definitions.get(id) ?? null);
            if (token !== openToken) return;
            if (!summon) throw new Error('Summon preview has no available creature');
            payload = { ...payload, summon, ...(payload.recipe.kind === 'none' ? { recipe: summon.rise } : {}) };
        }
        body.innerHTML = '';
        // The handle arrives before the mount's first await, so closing mid-load disposes the pending preview.
        await mod.mountSpellVfxPreview(body, payload, (handle) => {
            if (token === openToken) activeHandle = handle;
            else handle.dispose();
        });
    } catch (error) {
        modulePromise = null;
        if (token === openToken) body.innerHTML = '<p class="text-danger mb-0">Could not load the cast preview. Try again in a moment.</p>';
        console.error('[spell-vfx-preview]', error);
    }
}
